import type { GlobalStylesProps } from "@mui/material/GlobalStyles";
import theme, { createStyles, type SxStyles, ThemeColors, ThemeVars } from "core/utilities/theme";

const tableBodyBase: SxStyles = {
  "& tr:nth-of-type(even)": {
    "& td": {
      backgroundColor: "#fff",
    },
  },
  "& tr:nth-of-type(odd)": {
    "& td": {
      backgroundColor: ThemeColors.tableRowOther,
    },
  },
};

const yzebra: SxStyles = {
  "& th:nth-of-type(even):not(.withBackgroundColor)": {
    backgroundColor: `${ThemeColors.tableColumnOther} !important`,
  },
  "& td:nth-of-type(even):not(.withBackgroundColor)": {
    backgroundColor: `${ThemeColors.tableColumnOther} !important`,
  },
};

export default createStyles({
  tableWrapper: {
    overflowX: "auto",
  },
  tableWrapperFullscreen: {
    overflowY: "auto",
    height: "calc(100vh - 90px)", // 90 = table header and footer visible, remaining is scrolling
    boxShadow: `0 3px 0 0 ${ThemeColors.tableBorder}`,
  },
  fullscreen: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    zIndex: ThemeVars.fullScreenZIndex,
    background: "#fff",
  },
  fullscreenColumnWithColumnGroup: {
    position: "sticky",
    backgroundColor: "#fff",
    boxShadow: `3px 3px 0 0 ${ThemeColors.tableBorder}`,
    zIndex: 1203,
    padding: 0,
    verticalAlign: "top",
  },
  fullscreenColumnWithoutColumnGroup: {
    top: 0,
    position: "sticky",
    backgroundColor: "#fff",
    boxShadow: `3px 3px 0 0 ${ThemeColors.tableBorder}`,
    zIndex: 1203,
    padding: 0,
    verticalAlign: "top",
  },
  fullscreenColumnGroup: {
    top: 0,
    position: "sticky",
    backgroundColor: "#fff",
    boxShadow: `3px 0 0 0 ${ThemeColors.tableBorder}`,
    zIndex: 1203,
    verticalAlign: "middle",
  },
  fullscreenColumnHover: {
    zIndex: 1205,
    boxShadow: `0 3px 0 0 ${ThemeColors.tableBorder}`,
  },
  fullscreenFirstPinnedColumnGroup: {
    zIndex: 1205,
  },
  fullscreenLastPinnedColumn: {
    boxShadow: `3px 3px 0 0 ${ThemeColors.tableBorder}`,
  },
  fullscreenPinnedColumn: {
    zIndex: 1204,
  },
  fullscreenFirefoxBorderFixHeader: {
    bottom: "-2px",
  },

  table: {
    resize: "horizontal",
    position: "relative",
    // When an element has the position 'sticky' within a table
    // borders might not follow when scrolling, this fixes it
    // https://stackoverflow.com/questions/50361698/border-style-do-not-work-with-sticky-position-element
    borderCollapse: "separate",
    "& th": {
      backgroundColor: "#fff",
    },
    "& th:not(.withBackgroundColor)": {
      backgroundColor: "#fff !important",
    },
  },
  header: {
    height: "39px",
    padding: theme.spacing(0, 0.5, 0, 2),
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    backgroundColor: ThemeColors.tableHeader,
    borderBottom: `1px solid ${ThemeColors.tableBorder}`,
  },
  highlightedHeader: {
    backgroundColor: ThemeColors.selectable,
  },
  sectionBreadcrumb: {
    height: "30px",
    marginTop: theme.spacing(-0.5),
    "& span, & h1": {
      fontSize: theme.typography.body2.fontSize,
    },
    "& svg": {
      marginTop: theme.spacing(1),
    },
  },
  tbody: {
    ...tableBodyBase,
    "& tr:hover:not(.editable)": {
      // Some cells have a specific background color, we don't want to override that
      "& td:not(.withBackgroundColor):not(.editable)": {
        backgroundColor: `${ThemeColors.selectable} !important`,
      },
    },
    "& tr:not(.editable)": {
      "& td": {
        padding: theme.spacing(0.75, 2, 0.75, 1.5),
      },
      "& td:first-of-type": {
        padding: theme.spacing(0.75, 0, 0.75, 1),
      },
    },
    "& tr.editable": {
      "& td": {
        padding: theme.spacing(0, 0, 0, 1.5),
      },
      "& td:first-of-type": {
        padding: theme.spacing(0, 0.5, 0, 1),
      },
    },
  },
  tbodyLightTable: {
    ...tableBodyBase,
    "& tr:hover": {
      // Some cells have a specific background color, we don't want to override that
      "& td:not(.withBackgroundColor)": {
        backgroundColor: `${ThemeColors.textHover} !important`,
      },
    },
  },
  tableCompact: {
    "& tbody": {
      "& tr": {
        "& td": {
          padding: theme.spacing(0.25, 2, 0.25, 1.5),
          fontSize: ThemeVars.compactFontSize,
          lineHeight: ThemeVars.compactFontSizeLineHeight,
        },
        "& td:first-of-type": {
          padding: theme.spacing(0.1, 0, 0, 1.25),
        },
      },
    },
  },
  lightTableLarge: {
    ...yzebra,
    "& tr": {
      "& th": {
        padding: theme.spacing(0.5, 0.75, 0.5, 0.75),
      },
      "& td": {
        padding: theme.spacing(0.5, 0.75, 0.5, 0.75),
      },
    },
  },
  lightTableCompact: {
    "& tr": {
      "& th": {
        padding: theme.spacing(0.25, 0.75, 0.25, 0.75),
        fontSize: ThemeVars.compactFontSize,
        lineHeight: ThemeVars.compactFontSizeLineHeight,
      },
      "& td": {
        padding: theme.spacing(0.25, 0.75, 0.25, 0.75),
        fontSize: ThemeVars.compactFontSize,
        lineHeight: ThemeVars.compactFontSizeLineHeight,
      },
    },
  },
});

export const tablePopperStyles: GlobalStylesProps["styles"] = {
  // Hide popper when scrolling horizontally if the target is going out of the viewport
  ".autosuggestPopper[data-popper-reference-hidden]": {
    visibility: "hidden",
    pointerEvents: "none",
  },
};

// TODO: type missing
export const pinnedColumnStyles = {
  pinnedColumn: {
    position: "sticky",
    left: 0,
    zIndex: 1,
  },
  pinnedColumnGroup: {
    position: "sticky",
    zIndex: 1,
  },
  firstPinnedColumnGroup: {
    position: "sticky",
    left: 0,
    top: 0,
    zIndex: 2,
  },
  pinnedColumnHover: {
    position: "sticky",
    left: 0,
    zIndex: 2,
  },
  lastPinnedColumn: {
    boxShadow: `3px 0 0 0 ${ThemeColors.tableBorder};`,
  },
};

export const pinnedColumnClasses = createStyles(pinnedColumnStyles);
